import React, { useEffect, useState } from 'react';
import { GFLesson, GFQuizItem } from '../../models/models';
import Video from './resources/Video';
import SlideShow from './resources/SlideShow';
import LessonResourceSingleTabItem from './LessonResourceSingleTabItem';
import LessonResourceMultiTabItem from './LessonResourceMultiTabItem';
import Image from './resources/Image';
import utils from './utils';
import Locked from './resources/Locked';

type Props = {
  lesson: GFLesson,
  isResourcesLockedCallback: () => void,
  isResourcesLocked: boolean,
  preTest?: GFQuizItem,
  startQuiz: (gfQuiz: GFQuizItem) => void,
  isStudent: boolean,
}

export enum Tabs {
  Locked = 'locked',
  PrimaryVideo = 'primaryVideo',
  SlideShow = 'slideShow',
  SecondaryVideo = 'secondaryVideo',
  ShortVideo = 'shortVideo',
  MusicVideoAudio = 'musicVideoAudio',
  MemorableImages = 'memorableImages',
  DefinitionCards = 'definitionCards',
  WhyItIsImportant = 'whyItIsImportant',
  Kahoot = 'kahoot',
}

const LessonResources: React.FunctionComponent<Props> = ({ lesson, isResourcesLockedCallback, isResourcesLocked, preTest, startQuiz, isStudent }) => {
  const [currentTab, setCurrentTab] = useState<Tabs>(isResourcesLocked ? Tabs.Locked : Tabs.PrimaryVideo);
  const [currentItem, setCurrentItem] = useState<number>(0);

  useEffect(() => {
    setCurrentTab(isResourcesLocked ? Tabs.Locked : Tabs.PrimaryVideo);
  }, [isResourcesLocked]);

  const handleSelect = (name: Tabs) => {
    if (isResourcesLocked) {
      return isResourcesLockedCallback();
    }

    setCurrentTab(name);
  }

  const handleMultiSelect = (name: Tabs, index: number) => {
    if (isResourcesLocked) {
      return;
    }

    setCurrentTab(name);
    setCurrentItem(index);
  }

  return <section className={`lesson-resources ${isStudent ? 'student' : ''}`}>
    <section className="lesson-resource-view text-center">
      {currentTab === Tabs.Locked && <Locked preTest={preTest} startQuiz={startQuiz}/>}
      {currentTab === Tabs.PrimaryVideo && <Video resource={lesson.primaryVideoPath}/>}
      {currentTab === Tabs.SlideShow && <SlideShow resource={lesson.slideshowPath}/>}
      {currentTab === Tabs.SecondaryVideo && <Video resource={lesson.secondaryVideoPath || ''}/>}
      {currentTab === Tabs.ShortVideo && <Video resource={lesson.shortVideoPath || ''}/>}
      {currentTab === Tabs.DefinitionCards && <Image resource={utils.getItem(lesson.definitionCards, currentItem)}/>}
      {currentTab === Tabs.MemorableImages && <Image resource={utils.getItem(lesson.memorableImages, currentItem)}/>}
      {currentTab === Tabs.WhyItIsImportant && <Image resource={utils.getItem(lesson.whyItIsImportant, currentItem)}/>}
      {currentTab === Tabs.MusicVideoAudio && <Video resource={lesson.musicVideoAudioPath || ''}/>}
    </section>
    <section className={`lesson-resource-tabs ${isResourcesLocked ? 'menuLocked' : ''}`}>
      <LessonResourceSingleTabItem
        name={Tabs.PrimaryVideo}
        currentTab={currentTab}
        handleSelect={handleSelect}
      >
        <i className="fa fa-film" aria-hidden="true"></i>
        <span>Long-Form Video</span>
      </LessonResourceSingleTabItem>
      <LessonResourceSingleTabItem
        name={Tabs.SlideShow}
        currentTab={currentTab}
        handleSelect={handleSelect}
      >
        <i className="fa fa-television" aria-hidden="true"></i>
        <span>Slideshow</span>
      </LessonResourceSingleTabItem>
      {lesson.secondaryVideoPath && <LessonResourceSingleTabItem
        name={Tabs.SecondaryVideo}
        currentTab={currentTab}
        handleSelect={handleSelect}
      >
        <i className="fa fa-toggle-right" aria-hidden="true"></i>
        <span>Grammar-in-Context Video</span>
      </LessonResourceSingleTabItem>}
      {lesson.shortVideoPath && <LessonResourceSingleTabItem
        name={Tabs.ShortVideo}
        currentTab={currentTab}
        handleSelect={handleSelect}
      >
        <i className="fa fa-id-card-o" aria-hidden="true"></i>
        <span>Short-Form Video</span>
      </LessonResourceSingleTabItem>}
      {lesson.memorableImages.length > 0 && <LessonResourceMultiTabItem
        name={Tabs.MemorableImages}
        currentTab={currentTab}
        currentItem={currentItem}
        handleMultiSelect={handleMultiSelect}
        items={lesson.memorableImages}
      >
        <i className="fa fa-bolt" aria-hidden="true"></i>
        <span>Memorable Image</span>
      </LessonResourceMultiTabItem>}
      {lesson.definitionCards.length > 0 && <LessonResourceMultiTabItem
        name={Tabs.DefinitionCards}
        currentTab={currentTab}
        currentItem={currentItem}
        handleMultiSelect={handleMultiSelect}
        items={lesson.definitionCards}
      >
        <i className="fa fa-newspaper-o" aria-hidden="true"></i>
        <span>Definition Card</span>
      </LessonResourceMultiTabItem>}
      {lesson.whyItIsImportant.length > 0 && <LessonResourceMultiTabItem
        name={Tabs.WhyItIsImportant}
        currentTab={currentTab}
        currentItem={currentItem}
        handleMultiSelect={handleMultiSelect}
        items={lesson.whyItIsImportant}
      >
        <i className="fa fa-flag" aria-hidden="true"></i>
        <span>Why It's Important</span>
      </LessonResourceMultiTabItem>}
      {lesson.musicVideoAudioPath && <LessonResourceSingleTabItem
        name={Tabs.MusicVideoAudio}
        currentTab={currentTab}
        handleSelect={handleSelect}
      >
        <i className="fa fa-music" aria-hidden="true"></i>
        <span>Music Video</span>
      </LessonResourceSingleTabItem>}
    </section>
  </section>
}

export default LessonResources;
